// @givmo/design-system — Form integration (React Hook Form + Zod).
//
// A typed wrapper that wires the design-system form controls (TextField, Select, Switch,
// OtpField, MaskedInput, DatePicker, Checkbox, RadioTile) to React Hook Form with Zod
// validation, displaying field errors through the existing <ErrorMessage>.
//
// Shape (shadcn-style, adapted to the Givmo `testId` + `ErrorMessage` conventions):
//   - `useGivmoForm(schema, options)` — `useForm` pre-wired with `zodResolver(schema)` and the
//     schema's inferred types; returns the standard RHF form object.
//   - `<Form form={form} onSubmit={...}>` — a `<form>` that hosts the RHF FormProvider and
//     calls `form.handleSubmit(onSubmit)`.
//   - `<FormField name="..." render={({ field, error, invalid }) => ...}>` — a Controller that
//     provides the field bindings + the resolved error, and (unless `hideError`) renders an
//     inline <ErrorMessage> from the Zod message automatically.
//
// Re-exports `zodResolver` and `z` so consumers wire validation without importing the libs
// directly (keeps the validation engine swappable behind this module).
import type { ReactNode } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  type ControllerRenderProps,
  type DefaultValues,
  type FieldPath,
  type FieldValues,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { ErrorMessage } from './error-message';

/**
 * `useForm` pre-wired with a Zod resolver. The form values are inferred from the schema's
 * INPUT type (which must be an object — a `FieldValues`), so `form.handleSubmit` and
 * `<FormField name>` are fully typed against it.
 */
export function useGivmoForm<TValues extends FieldValues, TOutput = TValues>(
  schema: z.ZodType<TOutput, TValues>,
  options?: Omit<UseFormProps<TValues>, 'resolver'> & {
    defaultValues?: DefaultValues<TValues>;
  },
): UseFormReturn<TValues> {
  return useForm<TValues>({
    // zodResolver's Zod-4 overload accepts the schema directly.
    resolver: zodResolver(schema) as never,
    ...options,
  });
}

export interface FormProps<TValues extends FieldValues> extends TestIdProps {
  form: UseFormReturn<TValues>;
  onSubmit: SubmitHandler<TValues>;
  children: ReactNode;
  className?: string;
}

export function Form<TValues extends FieldValues>({
  form,
  onSubmit,
  children,
  className,
  testId,
}: FormProps<TValues>) {
  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-16', className)}
        {...testIdProp(testId)}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export interface FormFieldRenderArgs<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
> {
  field: ControllerRenderProps<TValues, TName>;
  /** Whether the field currently has a validation error. */
  invalid: boolean;
  /** The resolved error message (Zod), if any. */
  error?: string;
}

export interface FormFieldProps<TValues extends FieldValues, TName extends FieldPath<TValues>> {
  name: TName;
  render: (args: FormFieldRenderArgs<TValues, TName>) => ReactNode;
  /** Suppress the auto-rendered <ErrorMessage> (e.g. when the control shows its own). */
  hideError?: boolean;
  /** Optional testId base; the auto error row derives `${testId}-error`. */
  testId?: string;
}

export function FormField<TValues extends FieldValues, TName extends FieldPath<TValues>>({
  name,
  render,
  hideError = false,
  testId,
}: FormFieldProps<TValues, TName>) {
  return (
    <Controller<TValues, TName>
      name={name}
      render={({ field, fieldState }) => {
        const error = fieldState.error?.message;
        return (
          <>
            {render({ field, invalid: fieldState.invalid, error })}
            {!hideError && error && (
              <ErrorMessage testId={testId ? `${testId}-error` : undefined}>{error}</ErrorMessage>
            )}
          </>
        );
      }}
    />
  );
}

// Re-export so consumers build schemas + resolvers through the design-system surface.
export { zodResolver };
export type { SubmitHandler, UseFormReturn };
